import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Modal, Tree, Typography } from 'antd';
import moment, { Moment } from 'moment';

import monthlyReportService, { IAssetGroupOption } from '../../services/monthlyReportService';

const { MonthPicker } = DatePicker;
const { Text } = Typography;

type CheckedKeys = (string | number)[];

const getDefaultReportMonth = (): Moment => {
  return moment().subtract(1, 'month').startOf('month');
};

type Props = {
  visible: boolean;
  assetGroupId: string;
  assetGroupName: string;
  onClose: () => void;
  onSubmitted?: (jobId: string) => void;
};

const GenerateMonthlyReportModal: React.FC<Props> = ({
  visible,
  assetGroupId,
  assetGroupName,
  onClose,
  onSubmitted,
}) => {
  const [activeAssetGroup, setActiveAssetGroup] = useState<IAssetGroupOption | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Moment | null>(getDefaultReportMonth());
  const [checkedKeys, setCheckedKeys] = useState<CheckedKeys>([]);
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const optionsRequestRef = useRef(0);

  useEffect(() => {
    if (visible) {
      return;
    }
    // Clear previous asset-group tree state while modal is closed.
    setActiveAssetGroup(null);
    setCheckedKeys([]);
    setExpandedKeys([]);
    setLoadingOptions(false);
    setErrorText('');
  }, [visible]);

  useEffect(() => {
    if (!visible || !assetGroupId) {
      return;
    }
    const requestId = ++optionsRequestRef.current;
    let isMounted = true;
    const fetchOptions = async () => {
      setActiveAssetGroup(null);
      setCheckedKeys([]);
      setExpandedKeys([]);
      setLoadingOptions(true);
      setErrorText('');
      try {
        const response = await monthlyReportService.getOptions();
        if (!isMounted || optionsRequestRef.current !== requestId) {
          return;
        }
        const options = response?.asset_groups || [];
        const target = options.find((item) => item.asset_group_id === assetGroupId) || null;
        setActiveAssetGroup(target);
        if (target) {
          const groupKeys = target.groups.map((group) => `group:${group.id}`);
          const boxKeys = target.groups.flatMap((group) => group.sensorboxes.map((box) => `box:${box.id}`));
          setCheckedKeys([...groupKeys, ...boxKeys]);
          setExpandedKeys([]);
        } else {
          setCheckedKeys([]);
          setExpandedKeys([]);
        }
      } catch (error: any) {
        if (!isMounted || optionsRequestRef.current !== requestId) {
          return;
        }
        const errMsg = error?.response?.data?.detail || error?.message || 'Unable to load report options';
        setErrorText(errMsg);
      } finally {
        if (isMounted && optionsRequestRef.current === requestId) {
          setLoadingOptions(false);
        }
      }
    };

    setSelectedMonth(getDefaultReportMonth());
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [visible, assetGroupId]);

  const treeData = useMemo(() => {
    if (!activeAssetGroup) return [];
    return activeAssetGroup.groups.map((group) => ({
      title: group.name,
      key: `group:${group.id}`,
      children: group.sensorboxes.map((sensorbox) => ({
        title: sensorbox.name,
        key: `box:${sensorbox.id}`,
      })),
    }));
  }, [activeAssetGroup]);

  const selectedGroupIds = checkedKeys
    .filter((k) => String(k).startsWith('group:'))
    .map((k) => String(k).replace('group:', ''));
  const selectedSensorboxIds = checkedKeys
    .filter((k) => String(k).startsWith('box:'))
    .map((k) => String(k).replace('box:', ''));

  const submitJob = async () => {
    if (!selectedMonth) {
      return;
    }
    setSubmitting(true);
    setErrorText('');
    try {
      const payload = {
        asset_group_id: assetGroupId,
        month: Number(selectedMonth.format('M')),
        year: Number(selectedMonth.format('YYYY')),
        selected_group_ids: selectedGroupIds,
        selected_sensorbox_ids: selectedSensorboxIds,
      };
      const result = await monthlyReportService.createJob(payload);
      if (onSubmitted) {
        onSubmitted(result.job_id);
      }
      onClose();
    } catch (error: any) {
      const errMsg = error?.response?.data?.detail || error?.message || 'Failed to start report generation';
      setErrorText(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={<span style={{ color: '#22515a', fontWeight: 600 }}>{`Generate Monthly Report - ${assetGroupName || assetGroupId}`}</span>}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={720}
      bodyStyle={{ background: '#f7fbfb' }}
    >
      {errorText ? <Alert type="error" message={errorText} showIcon style={{ marginBottom: 12 }} /> : null}

      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text strong style={{ color: '#22515a' }}>Month:</Text>
        <MonthPicker
          style={{ flex: 1, borderColor: 'rgba(79, 140, 146, 0.5)' }}
          value={selectedMonth as any}
          onChange={(value) => setSelectedMonth(value)}
          allowClear={false}
          inputReadOnly
          format="MMMM YYYY"
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement || document.body}
          dropdownClassName="monthly-report-month-dropdown"
          popupStyle={{ zIndex: 1700 }}
        />
      </div>

      <div style={{ border: '1px solid rgba(79, 140, 146, 0.35)', borderRadius: 6, padding: 12, marginBottom: 12, background: 'rgba(79, 140, 146, 0.03)' }}>
        <Text strong style={{ color: '#22515a' }}>Sensorbox Groups / Sensorboxes</Text>
        <Tree
          checkable
          selectable
          selectedKeys={[]}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys as (string | number)[])}
          onSelect={(_, info) => {
            const key = info.node.key;
            if (!info.node.children || info.node.children.length === 0) {
              return;
            }
            setExpandedKeys((prev) => (
              prev.includes(key)
                ? prev.filter((item) => item !== key)
                : [...prev, key]
            ));
          }}
          checkedKeys={checkedKeys}
          onCheck={(keys) => setCheckedKeys(keys as CheckedKeys)}
          treeData={treeData}
          style={{ marginTop: 10, maxHeight: 280, overflowY: 'auto' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92' }}
          loading={submitting || loadingOptions}
          onClick={submitJob}
          disabled={loadingOptions || !activeAssetGroup || selectedSensorboxIds.length === 0}
        >
          Generate Report
        </Button>
      </div>
    </Modal>
  );
};

export default GenerateMonthlyReportModal;